import PageLayout from '@/components/feature/PageLayout';
import Seo from '@/components/feature/Seo';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import JsonLd from '@/components/feature/JsonLd';
import { site } from '@/mocks/site';

const principles = [
  {
    icon: 'ri-user-3-line',
    title: '匿名 · 不指向真人',
    text: '所有人物均做匿名化与脱敏处理，绝不把站内故事与现实中的同学、老师、朋友一一对应，保护每个被记录的青春。',
  },
  {
    icon: 'ri-shield-check-line',
    title: '虚构 · 娱乐化叙事',
    text: '内容以虚构与艺术加工为主，带有娱乐与夸张成分，请当作轻松有趣的「段子型盘点」阅读，不恶意造谣中伤。',
  },
  {
    icon: 'ri-sun-line',
    title: '阳光 · 有温度',
    text: '我们希望每篇盘点都能让人会心一笑或心生暖意，传递校园生活的真实与可爱，而不是猎奇与伤害。',
  },
];

const stats = [
  { num: '6', label: '年记录' },
  { num: '1.2w+', label: '篇盘点被读' },
  { num: '5', label: '个常设栏目' },
];

export default function AboutPage() {
  return (
    <PageLayout>
      <Seo
        title="关于我们 · 校园爆点"
        description="了解校园爆点：一个专注校园风云人物、班长学委、社团名场面与反差瞬间的泛娱乐内容站，坚持匿名、虚构、阳光的内容原则。"
        keywords="校园爆料,关于我们,校园人物盘点"
      />
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'AboutPage',
          name: '关于校园爆点',
          description: site.desc,
        }}
      />

      <section className="bg-cream-100">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 py-10 md:px-6 md:py-16 lg:grid-cols-2">
          <div>
            <Breadcrumbs items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-500 px-3 py-1 text-xs font-bold text-cream-50">
              <i className="ri-hand-heart-line" /> 校园爆点编辑部
            </span>
            <h1 className="mt-4 font-display text-3xl leading-tight text-ink md:text-4xl">
              把校园里那些
              <span className="text-primary-500">值得被记住</span>的小事写下来
            </h1>
            <p className="mt-5 text-base leading-relaxed text-ink-600">{site.desc}</p>
            <p className="mt-3 text-base leading-relaxed text-ink-600">
              我们是一群热爱记录的学生视角观察者，相信每一间教室、每一个社团、每一顶抛起的学士帽背后，都藏着值得分享的青春切片。
            </p>
            <div className="mt-7 grid max-w-md grid-cols-3 gap-4">
              {stats.map((s) => (
                <div key={s.label} className="rounded-2xl border border-cream-200 bg-white p-4 text-center">
                  <p className="font-display text-2xl text-primary-600">{s.num}</p>
                  <p className="text-xs text-ink-600">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="overflow-hidden rounded-[2rem] border-4 border-white shadow-soft">
            <img
              src="https://readdy.ai/api/search-image?query=Flat%20vector%20illustration%20of%20a%20friendly%20team%20of%20young%20student%20editors%20around%20a%20table%20with%20laptops%20and%20sticky%20notes%2C%20warm%20coral%20amber%20and%20rose%20palette%2C%20cozy%20editorial%20zine%20style%2C%20clean%20cream%20background&width=760&height=560&seq=about-team&orientation=landscape"
              alt="校园爆点编辑部团队围坐创作的插画"
              className="w-full object-cover"
            />
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
        <div className="mb-8 text-center">
          <h2 className="font-display text-2xl text-ink md:text-3xl">我们的内容原则</h2>
          <p className="mt-2 text-sm text-ink-600">好看的前提是善良，吃瓜的底线是不伤害任何人</p>
        </div>
        <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
          {principles.map((p) => (
            <div key={p.title} className="rounded-2xl border border-cream-200 bg-white p-6">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary-100 text-2xl text-primary-600">
                <i className={p.icon} />
              </span>
              <h3 className="mt-4 text-base font-bold text-ink">{p.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-ink-600">{p.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-4 md:px-6">
        <div className="rounded-2xl bg-cream-100 p-6 md:p-10">
          <h2 className="font-display text-2xl text-ink md:text-3xl">编辑部想对你说</h2>
          <p className="mt-4 max-w-3xl text-base leading-relaxed text-ink-600">
            做「校园爆点」的初衷很简单：上大学的日子里，真正让人记住的，往往不是书上的公式，而是那个自习时自带综艺感的同学、
            那个把 Excel 用到飞起的学委、那场让全校围观的社团游园，以及毕业照里那顶飞走的学士帽。我们想把这份热气腾腾的青春，
            用轻松的方式保存下来。
          </p>
          <p className="mt-3 max-w-3xl text-base leading-relaxed text-ink-600">
            如果你也有想分享的名场面或走心瞬间，欢迎随时把素材发给我们。我们保证：匿名记录、脱敏发布、认真对待每一份投稿，
            也绝不编造伤害任何人的内容。
          </p>
          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
            <a
              href={`mailto:${site.email}`}
              className="inline-flex w-fit items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-bold text-cream-50 hover:bg-primary-600"
            >
              <i className="ri-send-plane-line" /> 投稿 / 联系编辑部
            </a>
            <span className="flex items-center gap-2 text-sm text-ink-600">
              <i className="ri-mail-line text-primary-500" />
              {site.email}
            </span>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}